import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    RTEFormatter.addTableSemantics(table);

    const parent = table.parentNode;
    if (!parent || (parent instanceof HTMLElement && parent.classList.contains('rte-table-wrapper'))) return;

    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  }

  /**
   * Restores data table semantics for tables pasted into the rte without header cells.
   * Also runs for every table in the main content (see below), so templates don't need <rte-formatter> for it.
   * - Tables that already have <th> cells are left as they are.
   * - Cells in an existing <thead> are promoted to column headers.
   * - Otherwise a first row made only of bold cells is promoted to column headers.
   * - role="presentation" is removed from tables that end up with headers, since it hides the table semantics.
   * @param {HTMLTableElement} table
   */
  static addTableSemantics(table) {
    const rows = Array.from(table.rows);
    const hasHeaderCells = rows.some((row) => Array.from(row.cells).some((cell) => cell.tagName === 'TH'));

    if (!hasHeaderCells) {
      const headerRows = RTEFormatter.#findHeaderRows(table, rows);
      if (headerRows.length === 0) return;

      headerRows.forEach((row) => Array.from(row.cells).forEach(RTEFormatter.#convertToHeaderCell));

      if (!table.tHead) {
        const thead = table.createTHead();
        headerRows.forEach((row) => thead.appendChild(row));
      }
    }

    if (table.getAttribute('role') === 'presentation') {
      table.removeAttribute('role');
    }
  }

  /**
   * Finds the rows that act as column headers in a table without <th> cells
   * @param {HTMLTableElement} table
   * @param {HTMLTableRowElement[]} rows
   * @returns {HTMLTableRowElement[]}
   */
  static #findHeaderRows(table, rows) {
    if (rows.length < 2) return [];

    // An explicit <thead> is a clear signal, whatever the cell formatting
    if (table.tHead && table.tHead.rows.length > 0) {
      return Array.from(table.tHead.rows);
    }

    const firstRow = rows[0];
    const cells = Array.from(firstRow.cells);
    const columnCount = cells.reduce((total, cell) => total + cell.colSpan, 0);
    if (columnCount < 2 || firstRow.parentElement?.tagName === 'TFOOT') return [];

    const isBold = (/** @type {HTMLTableCellElement} */ cell) => {
      const text = (cell.textContent ?? '').replace(/\s+/g, '');
      if (!text) return true; // empty cells, such as a blank corner cell, don't disqualify the row
      const boldText = Array.from(cell.querySelectorAll('strong, b'))
        .map((el) => el.textContent ?? '')
        .join('')
        .replace(/\s+/g, '');
      return text === boldText;
    };
    const hasText = cells.some((cell) => cell.textContent?.trim());

    return hasText && cells.every(isBold) ? [firstRow] : [];
  }

  /**
   * Replaces a <td> with a column header <th>, keeping its attributes and content
   * @param {HTMLTableCellElement} cell
   */
  static #convertToHeaderCell(cell) {
    const th = document.createElement('th');
    Array.from(cell.attributes).forEach((attr) => th.setAttribute(attr.name, attr.value));
    th.scope = 'col';
    th.append(...cell.childNodes);
    cell.replaceWith(th);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

/**
 * Fixes table semantics for all content tables, including rich text rendered without <rte-formatter>
 * (for example a raw `{{ page.content }}` in a custom liquid block).
 * @param {ParentNode} root
 */
function addSemanticsToTables(root) {
  root.querySelectorAll('table').forEach((table) => RTEFormatter.addTableSemantics(table));
}

const initTableSemantics = () => {
  const main = document.getElementById('MainContent');
  if (main) addSemanticsToTables(main);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTableSemantics, { once: true });
} else {
  initTableSemantics();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) addSemanticsToTables(event.target);
});
